import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { viewPropertyList } from "../../Redux/Actions/propertyActions";
import { getEnums } from "../../Redux/Actions/enumsActions";
import { columnsProperties } from "../../Global Functions/columnsAntd";
import TableComponent from "../../Global Functions/tableComponents";
import toast from "react-hot-toast";
import DateRangePopup from "../../Utils/dateFilterPopup";

const ListProperties = () => {
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const { enumsData } = useSelector((state) => state.enumsData || {});
  const { properties, loading } = useSelector((state) => state.properties || {});
  const propStatus = enumsData?.data?.PropertyStatus;

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchDetails = useCallback(
    async (page, limit, showLoader = true) => {
      if (!propStatus) return;

      const payload = {
        status: propStatus,
        page,
        limit,
        is_deleted: false,
        filter: debouncedSearch,
        ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
        ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
      };

      try {
        await dispatch(viewPropertyList(payload, showLoader));
        if (!showLoader) {
          toast.success("Properties list refreshed");
        }
      } catch {
        if (!showLoader) {
          toast.error("Failed to refresh properties list");
        }
      }
    },
    [dispatch, debouncedSearch, propStatus, dateRange]
  );

  useEffect(() => {
    dispatch(getEnums());
  }, [dispatch]);

  useEffect(() => {
    fetchDetails(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, fetchDetails]);


  useEffect(() => {
    if (properties?.total) {
      setPagination((prev) => ({ ...prev, total: properties.total }));
    }
  }, [properties]);


  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery]);

  // 🔹 Silent auto refresh (30 min)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDetails(
        pagination.current,
        pagination.pageSize,
        false
      );
    }, 1800000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchDetails, pagination.current, pagination.pageSize]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleDateRangeApply = (start, end) => {
    if (start === null && end === null) {
      setDateRange({ start: null, end: null });
    } else {
      setDateRange({ start, end });
    }
    setIsDatePopupVisible(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const filterHandler = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  return (
    <>
      <TableComponent
        h3heading="Properties List"
        filterFunction={filterHandler}
        pagination={pagination}
        setState={setSearchQuery}
        dataSource={Array.isArray(properties?.data) ? properties.data : []}
        onChange={handleTableChange}
        onCalendarClick={() => setIsDatePopupVisible(true)}
        loading={loading}
        columns={columnsProperties}
      />

      <DateRangePopup
        visible={isDatePopupVisible}
        initialStart={dateRange.start}
        initialEnd={dateRange.end}
        onCancel={() => setIsDatePopupVisible(false)}
        onOk={handleDateRangeApply}
      />
    </>
  );
};

export default ListProperties;
